import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, RotateCcw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DEFAULT_STATUSES,
  STATUS_CATEGORIES,
  STATUS_COLORS,
  canonicalStatusKey,
  notifyStatusesChanged,
  slugifyStatus,
} from './projectStatuses';

const selectClass = 'h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100';

export default function BoardStatusManager({ open, onOpenChange, api, boardId, statuses }) {
  const [rows, setRows] = useState([]);
  const [replace, setReplace] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(statuses.map(status => ({ ...status, existing: true })));
      setReplace({});
    }
  }, [open, statuses]);

  const removed = useMemo(
    () => statuses.filter(original => !rows.some(row => row.key === original.key)),
    [rows, statuses]
  );

  const updateRow = (index, patch) => setRows(current => current.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));

  const addRow = () => setRows(current => [...current, { key: '', label: '', color: 'purple', category: 'in_progress', existing: false }]);

  const removeRow = index => setRows(current => current.filter((_, rowIndex) => rowIndex !== index));

  const restoreDefaults = () => setRows(DEFAULT_STATUSES.map(status => ({ ...status, existing: statuses.some(original => original.key === status.key) })));

  const buildPayload = () => {
    const usedKeys = new Set(rows.filter(row => row.existing).map(row => canonicalStatusKey(row.key)));
    return rows.map(row => {
      if (row.key) return { key: row.key, label: row.label.trim(), color: row.color, category: row.category };
      let base = slugifyStatus(row.label) || 'status';
      let key = base;
      let suffix = 2;
      while (usedKeys.has(canonicalStatusKey(key))) key = `${base}_${suffix++}`;
      usedKeys.add(canonicalStatusKey(key));
      return { key, label: row.label.trim(), color: row.color, category: row.category };
    });
  };

  const handleSave = async () => {
    if (rows.some(row => !row.label.trim())) { toast.error('Dê um nome a todos os status'); return; }
    const labels = rows.map(row => row.label.trim().toLowerCase());
    if (new Set(labels).size !== labels.length) { toast.error('Há status com o mesmo nome'); return; }
    if (!rows.some(row => row.category === 'done')) { toast.error('Mantenha ao menos um status da categoria Concluído'); return; }

    const payload = buildPayload();
    const targets = {};
    removed.forEach(status => { targets[status.key] = replace[status.key] || payload[0].key; });

    setSaving(true);
    try {
      await api.put(`/projects/boards/${boardId}/statuses`, { statuses: payload, replace: targets });
      toast.success('Status atualizados');
      notifyStatusesChanged(boardId);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Não foi possível salvar os status');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Status do quadro</DialogTitle>
          <DialogDescription>
            Crie os status que fazem sentido para o seu fluxo. A categoria define como cada um entra nos números do projeto (concluídos, atrasados, bloqueados).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {rows.map((row, index) => (
            <div key={row.key || `new-${index}`} className="rounded-xl border border-slate-200 bg-white p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`h-3 w-3 shrink-0 rounded-full ${(STATUS_COLORS[row.color] || STATUS_COLORS.slate).dot}`} />
                <Input
                  value={row.label}
                  onChange={event => updateRow(index, { label: event.target.value })}
                  maxLength={40}
                  placeholder="Nome do status"
                  className="h-9 min-w-[160px] flex-1"
                />
                <select value={row.category} onChange={event => updateRow(index, { category: event.target.value })} className={selectClass} title="Como este status conta nos números">
                  {STATUS_CATEGORIES.map(category => <option key={category.value} value={category.value}>{category.label}</option>)}
                </select>
                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  disabled={rows.length <= 1}
                  className="rounded-md p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                  title="Remover status"
                  aria-label={`Remover ${row.label || 'status'}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {Object.entries(STATUS_COLORS).map(([color, style]) => (
                  <button
                    key={color}
                    type="button"
                    title={style.label}
                    onClick={() => updateRow(index, { color })}
                    className={`h-5 w-5 rounded-full ${style.dot} ring-offset-2 transition-transform hover:scale-110 ${row.color === color ? 'ring-2 ring-slate-700' : ''}`}
                  />
                ))}
                <span className={`ml-2 rounded-full border px-3 py-0.5 text-xs font-semibold ${(STATUS_COLORS[row.color] || STATUS_COLORS.slate).chip}`}>{row.label || 'Prévia'}</span>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap items-center justify-between gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addRow}><Plus className="mr-1.5 h-4 w-4" />Adicionar status</Button>
            <button type="button" onClick={restoreDefaults} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-blue-600"><RotateCcw className="h-3.5 w-3.5" />Restaurar padrão</button>
          </div>

          {removed.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-semibold text-amber-800">Tarefas que usam status removidos serão movidas:</p>
              <div className="mt-2 space-y-2">
                {removed.map(status => (
                  <div key={status.key} className="flex flex-wrap items-center gap-2 text-sm text-amber-900">
                    <span className="min-w-[120px] font-medium">{status.label}</span>
                    <span>→</span>
                    <select
                      value={replace[status.key] || ''}
                      onChange={event => setReplace(current => ({ ...current, [status.key]: event.target.value }))}
                      className={selectClass}
                    >
                      <option value="">{rows[0]?.label || 'Primeiro status'} (padrão)</option>
                      {rows.filter(row => row.key).map(row => <option key={row.key} value={row.key}>{row.label}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="button" onClick={handleSave} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700">{saving ? 'Salvando...' : 'Salvar status'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
